import { isNullOrUndefined } from '@fiji/common/src/utils/helpers';

function handlePrimaryHeading(widgetType: any, t: any): string {
    switch (widgetType) {
        case 'device_list':
            return t('COMMON:SELECTED_DEVICES');
        case 'schedule':
            return t('COMMON:SELECTED_ASSETS');
        case 'group_list':
            return t('COMMON:SELECTED_GROUPS');
        case 'load_groups':
        case 'load_group_trends':
            return t('WIDGETS:LOADS_GROUPS.SELECTED_LOAD_GROUPS');
        case 'loads':
            return t('WIDGET_MANAGEMENT:SELECT_LOADS');
        default:
            return t('COMMON:SELECTED_GROUPS_AND_DEVICES');
    }
}

function handleSubHeading(widgetType: any, payload: any, t: any): string {
    switch (widgetType) {
        case 'schedule':
            return t('HELPERS_MSGS:SCHEDULE_MSG');
        case 'device_list':
            return '';
        case 'group_list':
            return t('HELPERS_MSGS:GROUP_LIST_MSG', {
                replace: { groupLength: payload?.selectedNodes?.length ?? 0 },
            });
        case 'loads':
            return t('HELPERS_MSGS:LOADS_MSG', {
                replace: { loadsLength: payload?.selectedNodes?.length ?? 0 },
            });
        case 'upcoming':
        case 'environmental_stress_monitoring':
            return t('HELPERS_MSGS:UPCOMING_MSG', {
                replace: { upcomingLength: payload?.maxCount ?? 6 },
            });
        case 'load_groups':
        case 'load_group_trends':
            return t('WIDGETS:COMMON.REARRANGE_CHANNELS_DESCRIPTION', {
                channelType: t('WIDGETS:BALANCING.TARGET_LOAD_GROUPS'),
                count: `(${payload?.maxCount ?? 6})`,
            });
        case 'timeline':
        default:
            return t('HELPERS_MSGS:TIMELINE_MSG', {
                replace: { timelineLength: payload?.selectedNodes?.length ?? 0 },
            });
    }
}

const getStringGroupPath = (pathName: any[], deviceName: any): string => {
    let path = '';
    if (Array.isArray(pathName)) {
        if (deviceName) path = `${path}${deviceName}`;
        if (pathName?.length > 0 && deviceName) path = `${path} <`;
        const groupPath: any = [...pathName];
        groupPath.forEach((groupName: string, index: number): void => {
            path = `${path} ${groupName}`;
            if (index !== groupPath?.length - 1) path = `${path} <`;
        });
    }
    return path;
};

const getChartMaxValue = (newData: any): any => {
    const sortedData = (a: any, b: any): any => {
        const aVal = a?.value ? +a.value : 0;
        const bVal = b?.value ? +b.value : 0;
        if (aVal > bVal) return -1;
        if (aVal < bVal) return 1;
        return 0;
    };
    return newData?.sort?.(sortedData)[0];
};

const getIntervalValue = (dataValue: any): any => {
    let value = 0;
    if (isNaN(dataValue)) value = getChartMaxValue(dataValue)?.value;
    else value = dataValue ?? 0;
    if (value > 10) return Math.ceil(value / 10);
    return value ? (value / 12)?.toFixed?.(2) : undefined;
};

const isHighAndLow = (threshold: any): any => {
    const high = ['highCritical', 'highWarning'];
    const low = ['lowCritical', 'lowWarning'];
    const thresholdKeys = Object.keys(threshold ?? {});
    return high.find((key) => thresholdKeys?.includes(key)) && low.find((key) => thresholdKeys?.includes(key));
};

const getFilteredThresholds = (arrThreshold: string[], isHigh: boolean, threshold: any, value: number): any[] =>
    arrThreshold
        .map((item: string) => ({ name: item, value: threshold[item] }))
        .filter((item) => (isHigh ? item.value <= value : item.value >= value));

const getHighestThresholdName = (allMaxValue: any[]): string =>
    allMaxValue.reduce((maxItem, currentItem) => (currentItem.value > maxItem.value ? currentItem : maxItem)).name;

const findColor = (threshold: any, value: number): any => {
    const arrThreshold = Object.keys(threshold ?? {});
    const isHigh = Boolean(arrThreshold?.[0]?.includes('high'));
    const allMaxValue = getFilteredThresholds(arrThreshold, isHigh, threshold, value);
    if (!allMaxValue.length) return '#55BF3B';
    const highestValName = getHighestThresholdName(allMaxValue);
    if (highestValName === 'lowWarning' || highestValName === 'highWarning') return '#DDDF0D';
    if (highestValName === 'lowCritical' || highestValName === 'highCritical') return '#DF5353';
    return '#55BF3B';
};

const buildThresholdBands = (
    lowCritical: any,
    lowWarning: any,
    highWarning: any,
    highCritical: any,
    criticalColor: string,
    warningColor: string,
    middleColor: string
): any[] => [
    ...(!isNullOrUndefined(lowCritical) ? [{ from: 0, to: lowCritical, color: criticalColor, thickness: 70 }] : []),
    ...(!isNullOrUndefined(lowWarning)
        ? [{ from: lowCritical ?? 0, to: lowWarning, color: warningColor, thickness: 70 }]
        : []),
    { from: lowWarning ?? lowCritical ?? 0, to: highWarning ?? highCritical ?? 9e9, color: middleColor, thickness: 70 },
    ...(!isNullOrUndefined(highWarning)
        ? [{ from: highWarning, to: highCritical || 9e9, color: warningColor, thickness: 70 }]
        : []),
    ...(!isNullOrUndefined(highCritical) ? [{ from: highCritical, to: 9e9, color: criticalColor, thickness: 70 }] : []),
];

const generatePlotBands = (
    widgetData: any,
    isThreshold: boolean,
    isLowAndHigh?: boolean,
    isHorizontal?: boolean
): any => {
    const criticalColor = widgetData?.viewThreshold && isThreshold ? '#DF5353' : '#55BF3B';
    const warningColor = widgetData?.viewThreshold && isThreshold ? '#DDDF0D' : '#55BF3B';
    const highWarning = widgetData?.threshold?.highWarning;
    const highCritical = widgetData?.threshold?.highCritical;
    const lowWarning = widgetData?.threshold?.lowWarning;
    const lowCritical = widgetData?.threshold?.lowCritical;
    if (isHorizontal && !isLowAndHigh) {
        return [
            {
                from: 0,
                to: widgetData?.data?.value ?? 0,
                color: isThreshold ? findColor(widgetData?.threshold, widgetData?.data?.value) : '#55BF3B',
                thickness: 70,
            },
            { from: widgetData?.data?.value ?? 0, to: 9e9, color: '#eeeeee', thickness: 70 },
        ];
    }
    const getMiddleColor = (): string => {
        if (highCritical === 0) return criticalColor;
        if (highWarning === 0) return warningColor;
        return '#55BF3B';
    };

    if (widgetData?.viewThreshold && isThreshold)
        return buildThresholdBands(
            lowCritical,
            lowWarning,
            highWarning,
            highCritical,
            criticalColor,
            warningColor,
            getMiddleColor()
        );

    return [{ from: 0, to: 9e9, color: '#55BF3B', thickness: 70 }];
};

// Immutably filters `primary`/`secondary` device-channel lists to only the devices +
// channels whitelisted by `selectedWidgetEntities`. No-op unless `multiDevice === true`.
const filterMultiDeviceConfigByEntities = (
    config: any,
    selectedWidgetEntities: Array<{ deviceId?: string; entityIds?: string[] }> | undefined
): any => {
    if (!config || config.multiDevice !== true || !Array.isArray(selectedWidgetEntities)) {
        return config;
    }

    const allowedByDevice = new Map<string, Set<string>>();
    selectedWidgetEntities.forEach((entity) => {
        if (entity?.deviceId) {
            allowedByDevice.set(entity.deviceId, new Set(Array.isArray(entity.entityIds) ? entity.entityIds : []));
        }
    });

    const filterDeviceList = (list: unknown): any => {
        if (!Array.isArray(list)) return list;
        const next: any[] = [];
        list.forEach((item: any) => {
            const allowedChannelIds = allowedByDevice.get(item?.deviceId);
            if (!allowedChannelIds) return;
            if (!Array.isArray(item?.channels)) {
                next.push(item);
                return;
            }
            const filteredChannels = item.channels.filter(
                (channel: any) => channel?.channelId && allowedChannelIds.has(channel.channelId)
            );
            if (filteredChannels.length) {
                next.push({ ...item, channels: filteredChannels });
            }
        });
        return next;
    };

    const hasPrimary = Array.isArray(config.primary);
    const hasSecondary = Array.isArray(config.secondary);
    if (!hasPrimary && !hasSecondary) return config;

    return {
        ...config,
        ...(hasPrimary && { primary: filterDeviceList(config.primary) }),
        ...(hasSecondary && { secondary: filterDeviceList(config.secondary) }),
    };
};

const handleCleanTimelineDeviceListAndGroupListPayload = (configClone: any): void => {
    if (!configClone['multiDevice']) {
        delete configClone['filters'];
    }
    if (Array.isArray(configClone?.preferences) && !configClone?.preferences?.length) {
        delete configClone['preferences'];
    }
    delete configClone['selectedNodes'];
    delete configClone['secondary'];
    delete configClone['statusCount'];
};

const handleCleanMapPayload = (configClone: any): void => {
    delete configClone['selectedNodes'];
    delete configClone['markers'];
};

const handleCleanDefaultComponentPayload = (configClone: any): void => {
    if (configClone['primary']) {
        configClone['primary']?.forEach((item: any) => {
            item?.channels?.forEach((subItem: any) => {
                delete subItem?.value;
            });
        });
    }
    if (configClone['secondary']) {
        configClone['secondary']?.forEach((item: any) => {
            item?.channels?.forEach((subItem: any) => {
                delete subItem?.value;
            });
        });
    }

    if (!configClone['secondary']?.length) {
        delete configClone['secondary'];
    }
};

const handleCleanTrendsPayload = (configClone: any): void => {
    configClone['secondary']?.forEach((item: any) => {
        delete item?.data;
    });
    if (configClone['trendsLegendData']) delete configClone['trendsLegendData'];
};

const handleCleanLoadsPayload = (configClone: any): void => {
    if (!configClone['multiDevice']) {
        delete configClone['selectedNodes'];
        configClone['secondary'] = configClone['secondary']?.map((item: any) => item.loadId);
    } else {
        configClone['selectedNodes'] = configClone['selectedNodes']?.map((item: any) => ({
            loadId: item.loadId,
            deviceId: item.deviceId,
        }));
        delete configClone['secondary'];
    }
};

const handleCleanLoadGroupsPayload = (configClone: any): void => {
    if (configClone['trendsLegendData']) {
        delete configClone['trendsLegendData'];
    }
    if (configClone['loadGroupTrends']) {
        delete configClone['loadGroupTrends'];
    }
    if (!configClone['multiDevice']) {
        delete configClone['selectedNodes'];
        configClone['secondary'] = configClone['secondary']?.map((item: any) => ({
            ...item,
            loadConfig: [],
        }));
    } else {
        configClone['selectedNodes'] = configClone['secondary']?.map((item: any) => ({
            ...item,
            loadConfig: [],
        }));
        delete configClone['secondary'];
    }
};

export {
    handlePrimaryHeading,
    handleSubHeading,
    getStringGroupPath,
    generatePlotBands,
    getChartMaxValue,
    getIntervalValue,
    isHighAndLow,
    findColor,
    handleCleanDefaultComponentPayload,
    handleCleanLoadsPayload,
    handleCleanMapPayload,
    handleCleanTimelineDeviceListAndGroupListPayload,
    handleCleanTrendsPayload,
    handleCleanLoadGroupsPayload,
    filterMultiDeviceConfigByEntities,
};
